import { Controller } from '@hotwired/stimulus';
import { useCollectionEventListeners, useEventListener } from 'stimulus-library/dist';

export default class extends Controller {
  static targets = [
    'closeButton',
    'clickInside',
  ];

  static values = {
    name: String,
    clickableSelector: String,
    clickableParentSelector: String,
    open: {
      type: Boolean,
      default: false,
    },
    eventDriven: {
      type: Boolean,
      default: false,
    },
    dataTracking: Object,
  };

  get clickable() {
    if (!this.hasClickableParentSelectorValue || this.clickableParentSelectorValue === '') {
      return this.element.parentElement;
    }
    if (this.hasClickableSelectorValue && this.clickableSelectorValue === '') {
      return document.querySelector(this.clickableSelectorValue);
    }

    return this.element.closest(this.clickableParentSelectorValue);
  }

  initialize() {
    this.open = this.open.bind(this);
    this.onClick = this.onClick.bind(this);
    this.onKeyup = this.onKeyup.bind(this);
    this.close = this.close.bind(this);
    this.consumeInnerClick = this.consumeInnerClick.bind(this);
  }

  openValueChanged() {
    if (this.openValue) {
      this.open();
    } else {
      this.close();
    }
  }

  connect() {
    if (this.eventDrivenValue === false) {
      useEventListener(this, this.clickable, 'click', this.open);
      useEventListener(this, this.clickInsideTarget, 'click', this.consumeInnerClick);
      this.clickable.classList.add('cursor-pointer');
      if (this.hasCloseButtonTarget) {
        useCollectionEventListeners(this, this.closeButtonTargets, 'click', this.close);
      }
    }
  }

  open() {
    // if modal is already open, close it
    if (this.element.open) {
      this.close();
      return;
    }

    this.element.showModal();
    useEventListener(this, document.body, 'keyup', this.onKeyup);
    const { teardown } = useEventListener(this, document.body, 'click', this.onClick);
    this.teardownClickOutsideListener = teardown;

    if (this.hasNameValue && window.analytics) {
      const windowInfo = {
        url: window.location.href,
        search: window.location.search,
        path: window.location.pathname,
        title: document.title,
        name: this.nameValue,
      };

      window.analytics.track(`${this.nameValue} Modal Opened`, { ...windowInfo, ...this.dataTrackingValue });
    }
  }

  close(event) {
    if (event) {
      event.preventDefault();
      event.stopPropagation();
    }
    if (this.teardownClickOutsideListener) {
      this.teardownClickOutsideListener();
    }
    document.body.removeEventListener('keyup', this.onKeyup);

    // Hide the modal
    this.element.close();
  }

  onKeyup(event) {
    if (event.keyCode === 27) {
      // Escape key
      this.close(event);
    }
  }

  onClick(event) {
    if (this.element.contains(event.target)) {
      return;
    }

    // Check if the click was outside the modal
    const rect = event.target.getBoundingClientRect();
    if (rect.left > event.clientX
      || rect.right < event.clientX
      || rect.top > event.clientY
      || rect.bottom < event.clientY
    ) {
      this.close(event);
    }
  }

  consumeInnerClick(event) {
    event.stopPropagation();
  }
}
